<script setup lang="ts">
import type { GetCompanyJobsNotificationSettingsJobNotificationSettings } from '@swipe4work/models-orval'
import JobNotificationsContent from './components/JobNotificationsContent.vue'
import JobsNotificationSettingsDialog from './dialogs/JobsNotificationSettingsDialog.vue'
import { useGetCompanyJobsNotificationSettings } from '@swipe4work/api-orval'

definePage({
  name: '/settings/JobNotifications',
  path: '/instellingen/vacaturemeldingen',
  meta: {
    layout: 'false',
  },
})

const route = useRoute()
const router = useRouter()
const dialog = useDynamicDialog()

const { data, isPending } = useGetCompanyJobsNotificationSettings()

const jobs = computed(() => data.value?.jobs ?? [])
const teammates = computed(() => data.value?.teammates ?? [])

const selection = ref<GetCompanyJobsNotificationSettingsJobNotificationSettings[]>([])

// After a save the query is invalidated; re-point the selection to the fresh row objects
watch(jobs, freshJobs => {
  const selectedIds = new Set(selection.value.map(job => job.id))

  selection.value = freshJobs.filter(job => selectedIds.has(job.id))
})

// Keep the search in the URL so it survives back-navigation and reload
const search = ref(typeof route.query.search === 'string' ? route.query.search : '')

watch(search, value => {
  router.replace({
    query: {
      ...route.query,
      search: value || undefined, 
    }, 
  })
})

const openDialog = (jobIds: number[], initialSelectedEmployerIds: number[]) => {
  dialog.open(JobsNotificationSettingsDialog, {
    props: {
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: {
      jobIds,
      teammates: teammates.value,
      initialSelectedEmployerIds,
    },
  })
}

const openEditDialog = (job: GetCompanyJobsNotificationSettingsJobNotificationSettings) => {
  openDialog([ job.id ], job.subscribedEmployerIds)
}

const openBulkEditDialog = () => {
  // Pre-select the teammates that already follow every selected job
  const initialSelectedEmployerIds = teammates.value
    .filter(teammate => selection.value.every(job => job.subscribedEmployerIds.includes(teammate.id)))
    .map(teammate => teammate.id)

  openDialog(selection.value.map(job => job.id), initialSelectedEmployerIds)
}
</script>

<template>
  <JobNotificationsContent
    v-model:selection="selection"
    v-model:search="search"
    :jobs="jobs"
    :teammates="teammates"
    :is-pending="isPending"
    @edit="openEditDialog"
    @bulk-edit="openBulkEditDialog"
  />
</template>
